import { useEffect, useState } from 'react';
import { useShow, useCustomMutation, useGetIdentity, useInvalidate } from '@refinedev/core';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import type { TfUserRow } from '@verybigthings/trackframe-shared/schemas';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { fmtIso } from '@/lib/format';
import { cn } from '@/lib/utils';
import { UserMemory } from './Memory';
import { UserTracksTab } from './UserTracksTab';
import { hub } from '@/lib/hub-client';

// Tab-name segment that the App.tsx route allows after `show/:id/`. Each
// sub-tab adds to this set + the route in App.tsx.
const SUB_TABS = ['memory', 'tracks'] as const;
type Tab = 'details' | (typeof SUB_TABS)[number];

function isSubTab(value: string): value is (typeof SUB_TABS)[number] {
  return (SUB_TABS as readonly string[]).includes(value);
}

function tabFromPath(pathname: string, userId: string | undefined): Tab {
  if (!userId) return 'details';
  const segment = pathname.split('/').pop() ?? '';
  return pathname.startsWith(`/admin/users/show/${userId}/`) && isSubTab(segment) ? segment : 'details';
}

export function UsersShow() {
  const params = useParams<{ id: string }>();
  // Pass resource + id explicitly so Refine doesn't infer them from the URL.
  // The component is mounted under both `/admin/users/show/:id` (matches the
  // registered resource path) and `/admin/users/show/:id/memory` (does NOT
  // match) — without these props syncWithLocation can't resolve `id` on the
  // memory sub-path and `useShow` stays in isLoading or resets `user` to
  // undefined when the user clicks the Memory tab (plan-9 follow-up).
  const { query } = useShow<TfUserRow>({ resource: 'users', id: params.id });
  const user = query.data?.data;
  const invalidate = useInvalidate();
  const { mutate: customMutate, mutation } = useCustomMutation();
  const isPending = mutation.isPending;
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [confirmMfaReset, setConfirmMfaReset] = useState(false);
  const [mfaResetPending, setMfaResetPending] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { data: identity } = useGetIdentity<TfUserRow>();
  const adminUserId = identity?.id;

  // URL <-> tab sync: `/admin/users/show/:id/memory` -> "memory" tab,
  // `/admin/users/show/:id` -> "details" tab. Updating the tab pushes a
  // history entry so the Back button toggles tabs as expected.
  const initialTab = tabFromPath(location.pathname, params.id);
  const [tab, setTab] = useState<Tab>(initialTab);
  useEffect(() => {
    const next = tabFromPath(location.pathname, params.id);
    setTab(next);
  }, [location.pathname, params.id]);
  const handleTabChange = (next: string) => {
    const value: Tab = isSubTab(next) ? next : 'details';
    setTab(value);
    if (!params.id) return;
    const base = `/admin/users/show/${params.id}`;
    const target = value === 'details' ? base : `${base}/${value}`;
    if (location.pathname !== target) navigate(target, { replace: false });
  };

  if (query.isLoading) return <div>Loading…</div>;
  if (!user) return <div>Not found.</div>;

  const refresh = () =>
    invalidate({ resource: 'users', id: user.id, invalidates: ['detail', 'list'] });

  // HUB-121 D7: remove the user's second factor and sign them out everywhere.
  const resetMfa = async () => {
    setMfaResetPending(true);
    try {
      await hub.usersResetMfa({ params: { id: user.id } });
      setConfirmMfaReset(false);
      refresh();
    } finally {
      setMfaResetPending(false);
    }
  };

  const toggle = () => {
    const action = user.disabledAt ? 'reactivate' : 'disable';
    customMutate(
      {
        url: `users/${user.id}/${action}`,
        method: 'post',
        values: {},
      },
      {
        onSuccess: () => {
          setConfirmDisable(false);
          refresh();
        },
      },
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{user.email}</h1>
          <p className="text-sm text-muted-foreground">{user.name ?? '—'}</p>
        </div>
        <div className="flex gap-2">
          <Link to={`/admin/users/edit/${user.id}`} className={cn(buttonVariants({ variant: 'outline' }))}>
            Edit
          </Link>
          {user.mfa?.enrolled && (
            <Button variant="outline" onClick={() => setConfirmMfaReset(true)} disabled={mfaResetPending}>
              Reset MFA
            </Button>
          )}
          {user.disabledAt ? (
            <Button onClick={toggle} disabled={isPending}>
              {isPending ? 'Working…' : 'Reactivate'}
            </Button>
          ) : (
            <Button variant="destructive" onClick={() => setConfirmDisable(true)} disabled={isPending}>
              Disable
            </Button>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="memory">Memory</TabsTrigger>
          <TabsTrigger value="tracks">Tracks</TabsTrigger>
        </TabsList>
        <TabsContent value="details">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Details</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-y-2 text-sm md:grid-cols-2">
              <div className="text-muted-foreground">ID</div>
              <div className="font-mono text-xs">{user.id}</div>
              <div className="text-muted-foreground">Roles</div>
              <div className="flex flex-wrap gap-1">
                {user.roles.length === 0
                  ? '—'
                  : user.roles.map((r) => <Badge key={r} variant="secondary">{r}</Badge>)}
              </div>
              <div className="text-muted-foreground">Email verified</div>
              <div>{fmtIso(user.emailVerifiedAt)}</div>
              <div className="text-muted-foreground">Created</div>
              <div>{fmtIso(user.createdAt)}</div>
              <div className="text-muted-foreground">Updated</div>
              <div>{fmtIso(user.updatedAt)}</div>
              <div className="text-muted-foreground">Authenticator app</div>
              <div>{user.mfa?.enrolled ? <Badge variant="secondary">on</Badge> : '—'}</div>
              <div className="text-muted-foreground">Status</div>
              <div>
                {user.disabledAt
                  ? <Badge variant="destructive">disabled {fmtIso(user.disabledAt)}</Badge>
                  : <Badge variant="outline">active</Badge>}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="memory">
          <UserMemory userId={user.id} adminUserId={adminUserId} />
        </TabsContent>
        <TabsContent value="tracks">
          <UserTracksTab userId={user.id} />
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={confirmMfaReset}
        title="Reset MFA?"
        description={`${user.email} will sign in with their password only and every device will be signed out. They can set up a new authenticator app afterwards.`}
        confirmLabel="Reset MFA"
        destructive
        busy={mfaResetPending}
        onCancel={() => setConfirmMfaReset(false)}
        onConfirm={() => { void resetMfa(); }}
      />
      <ConfirmDialog
        open={confirmDisable}
        title="Disable user?"
        description={`${user.email} will be unable to sign in until reactivated.`}
        confirmLabel="Disable"
        destructive
        busy={isPending}
        onCancel={() => setConfirmDisable(false)}
        onConfirm={toggle}
      />
    </div>
  );
}
